// gti-chms-frontend/src/api/axiosConfig.ts
import axios from 'axios';
import { getAppNavigator } from '../services/navigation'; // Import the new utility

const instance = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add headers
instance.interceptors.request.use(
    (config) => {
        try {
            const churchIdentifier = import.meta.env.VITE_APP_CHURCH_IDENTIFIER;
            if (churchIdentifier) {
                config.headers['X-Church-Identifier'] = churchIdentifier;
            } else {
                console.warn("VITE_APP_CHURCH_IDENTIFIER is not set. Ensure your .env file is configured correctly for this deployment.");
            }

            const userString = localStorage.getItem('user');
            if (userString) {
                const user = JSON.parse(userString);
                if (user && user.token) {
                    config.headers.Authorization = `Bearer ${user.token}`;
                }
            }
        } catch (error) {
            console.error("Error setting Axios headers:", error);
            localStorage.removeItem('user');
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor
instance.interceptors.response.use(
    (response) => response,
    (error) => {
        // IMPORTANT: Check if the request was to the login endpoint
        // This ensures that failed login attempts (401/403) are handled by the LoginForm component itself
        // and do not trigger a global redirect.
        const isLoginAttempt = error.config.url?.endsWith('/auth/login'); // Use .endsWith for robustness

        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
            if (isLoginAttempt) {
                // If it's a login attempt, just reject the promise.
                // The LoginForm component's try-catch will handle displaying the error message.
                console.log("Authentication error during login attempt. Allowing component to handle.");
                return Promise.reject(error);
            } else {
                // For all other protected API calls that return 401/403:
                // This means the user's session is invalid (e.g., token expired, unauthorized access).
                console.error("Authentication error on protected route. Clearing session and redirecting.");
                localStorage.removeItem('user'); // Clear user data in localStorage

                const navigate = getAppNavigator(); // Get the globally available navigate function
                if (navigate) {
                    // Navigate to login page using React Router's navigate
                    navigate('/login', { replace: true, state: { from: window.location.pathname } });
                } else {
                    // Fallback if navigate is somehow not initialized (shouldn't happen with correct setup)
                    console.error("Navigator not initialized, falling back to window.location.href");
                    window.location.href = '/login';
                }
                // Important: Return a new rejected promise here to stop further processing of this error
                // in the component that initiated the API call, as we've already handled the global redirect.
                return new Promise(() => {}); // Return a never-resolving promise to prevent further execution
            }
        }
        // For other types of errors (e.g., 500, network errors), just reject the promise
        return Promise.reject(error);
    }
);

export default instance;
